/**
 * @file AdminInvoicesPage.tsx
 * @brief Super Admin → Invoices dashboard (platform-wide, all tenants).
 *
 * @details
 * The super-admin counterpart to the company-scoped invoices page. Lists every
 * billable invoice across all companies (Conversation, Meeting Intel, Personal
 * Notes) from the unified `GET /api/invoices` feed, which returns a
 * platform-wide result set for superadmin callers. Adds a Company column and a
 * tenant filter that the company page doesn't need.
 *
 * @dependencies
 * - ./api: useAdminInvoicesList, adminInvoicesExportUrl
 * - components/data/DataTable: paginated table shell
 * - features/analytics/components/StatCard: KPI tiles
 * - shared/api/client: apiClient (blob PDF/ZIP downloads)
 *
 * @references
 * - BACKEND/src/features/invoices/routes.js (superadmin branch of GET /, /export)
 * - WEBPANEL/src/features/company-invoices/CompanyInvoicesPage.tsx (sibling)
 *
 * @author DES
 * @date 08-07-2026
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Building2,
  Download,
  ExternalLink,
  FileArchive,
  LoaderCircle,
  PoundSterling,
  Receipt,
} from "lucide-react";
import {
  DataTable,
  type PaginationState,
} from "@/components/data/DataTable";
import type { TableRowGroup } from "@/components/data/groupRowsBy";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { MoneyAmount, MoneyBadges } from "@/shared/billing/MoneyCells";
import type { MoneyRowLike } from "@/shared/billing/moneyStatus";
import { Button } from "@/components/ui/Button";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { StatCard } from "@/features/analytics/components/StatCard";
import {
  GridBand,
  GridBlock,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import { PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { ApiClientError, apiClient } from "@/shared/api/client";
import { invoiceEndpoints } from "@/shared/api/endpoints";
import { toast } from "@/shared/hooks/useToast";
import {
  formatDateTime,
  formatFullName,
  formatMoneyMinor,
  getInitials,
} from "@/shared/utils/formatters";
import { companyGroupMeta } from "@/shared/utils/tableGroups";
import {
  adminInvoicesExportUrl,
  invoiceCompanyGroupKey,
  invoiceCompanyOwnerGroupKey,
  useAdminInvoicesGroupRows,
  useAdminInvoicesGroupSummary,
  useAdminInvoicesList,
} from "./api";
import {
  adminInvoicesPagination,
  invoiceCreatedAt,
  invoiceOwnerLabel,
  resolveInvoiceTotals,
  isMixedCurrency,
  type AdminInvoiceRow,
  type AdminInvoicesListParams,
  type InvoicePeriod,
} from "./schemas";
import { InvoiceMenu } from "@/components/feedback/InvoiceMenu";
import {
  AdminInvoicesDateNote,
  AdminInvoicesFilters,
  AdminInvoicesHeaderFilters,
} from "./components/AdminInvoicesFilters";

function paramsFromSearch(search: URLSearchParams): AdminInvoicesListParams {
  const period = search.get("period");
  return {
    page: Number(search.get("page")) || 1,
    pageSize: Number(search.get("pageSize")) || 25,
    q: search.get("q") || undefined,
    from: search.get("from") || undefined,
    to: search.get("to") || undefined,
    company_id: search.get("company_id") || undefined,
    period:
      period === "mtd" || period === "3m" || period === "all"
        ? period
        : "all",
  };
}

function searchFromParams(p: AdminInvoicesListParams): URLSearchParams {
  const usp = new URLSearchParams();
  if (p.page && p.page !== 1) usp.set("page", String(p.page));
  if (p.pageSize && p.pageSize !== 25) usp.set("pageSize", String(p.pageSize));
  if (p.q) usp.set("q", p.q);
  if (p.from) usp.set("from", p.from);
  if (p.to) usp.set("to", p.to);
  if (p.company_id) usp.set("company_id", p.company_id);
  if (p.period && p.period !== "all") usp.set("period", p.period);
  return usp;
}

export function AdminInvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => paramsFromSearch(searchParams), [searchParams]);
  // Row click opens that row's invoice menu (anchored to its button).
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const isGrouped = !params.company_id;
  /**
   * Grouped (no company picked): every company and user with an invoice,
   * from the server (`group=company_owner`), not grouped from loaded rows
   * (DES 01-10-2026: "haan superadmin pages pe bhi yehi kar do"). A user
   * group fetches its own invoices when opened, 25 at a time. The list call
   * stays for the two total cards (its totals cover every matching row),
   * asking for a single row.
   */
  const list = useAdminInvoicesList(isGrouped ? { ...params, page: 1, pageSize: 1 } : params);
  const summary = useAdminInvoicesGroupSummary(params, isGrouped);
  // Open groups (company and user keys) live in the URL (`open=key:batches,…`).
  const openParam = searchParams.get("open") ?? "";
  const openGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const part of openParam.split(",")) {
      const [key, n] = part.split(":");
      if (key) map.set(key, Math.max(1, Number(n) || 1));
    }
    return map;
  }, [openParam]);
  const setOpenGroups = (update: (prev: Map<string, number>) => Map<string, number>) => {
    const next = update(openGroups);
    const usp = new URLSearchParams(searchParams);
    const value = [...next].map(([k, n]) => (n > 1 ? `${k}:${n}` : k)).join(",");
    if (value) usp.set("open", value);
    else usp.delete("open");
    setSearchParams(usp, { replace: true });
  };
  // A search opens every group, so matches are visible.
  const searching = isGrouped && Boolean(params.q?.trim());
  // Only user (leaf) groups fetch rows.
  const openList = useMemo(() => {
    const keys = searching
      ? (summary.data ?? []).map((g) => invoiceCompanyOwnerGroupKey(g.company_id, g.owner_id))
      : [...openGroups.keys()].filter((k) => k.includes("/"));
    return keys.map((key) => ({ key, batches: openGroups.get(key) ?? 1 }));
  }, [searching, summary.data, openGroups]);
  const groupRows = useAdminInvoicesGroupRows(params, openList);
  const groups = useMemo<TableRowGroup<AdminInvoiceRow>[] | undefined>(() => {
    if (!isGrouped || !summary.data?.length) return undefined;
    const companies = new Map<string, TableRowGroup<AdminInvoiceRow>>();
    for (const g of summary.data) {
      const companyKey = invoiceCompanyGroupKey(g.company_id);
      let company = companies.get(companyKey);
      if (!company) {
        company = {
          key: companyKey,
          ...companyGroupMeta(g.company_name),
          rows: [],
          children: [],
          total: 0,
        };
        companies.set(companyKey, company);
      }
      const key = invoiceCompanyOwnerGroupKey(g.company_id, g.owner_id);
      const idx = openList.findIndex((o) => o.key === key);
      const q = idx >= 0 ? groupRows[idx] : undefined;
      const name = formatFullName(g.first_name, g.last_name);
      company.children!.push({
        key,
        label: name !== "—" ? name : g.email ?? "Unknown",
        avatarSrc: g.avatar_url,
        avatarInitials: getInitials(g.first_name, g.last_name, g.email),
        rows: q?.data?.items ?? [],
        total: g.count,
        loading: Boolean(q?.isFetching),
        error: Boolean(q?.isError),
        onRetry: () => void q?.refetch(),
      });
      company.total = (company.total ?? 0) + g.count;
    }
    const tree = [...companies.values()];
    for (const c of tree) c.children!.sort((a, b) => a.label.localeCompare(b.label));
    return tree.sort((a, b) => a.label.localeCompare(b.label));
  }, [isGrouped, summary.data, openList, groupRows]);
  const rows = useMemo(
    () =>
      isGrouped
        ? (groups ?? []).flatMap((c) => (c.children ?? []).flatMap((u) => u.rows))
        : list.data?.items ?? [],
    [isGrouped, groups, list.data?.items],
  );

  const paginationMeta = adminInvoicesPagination(list.data, params);
  const pagination: PaginationState | undefined =
    !isGrouped && list.data
      ? {
          page: paginationMeta.page,
          limit: paginationMeta.limit,
          total: paginationMeta.total,
          totalPages: paginationMeta.totalPages,
        }
      : undefined;

  const setParams = (next: AdminInvoicesListParams) =>
    setSearchParams(searchFromParams(next), { replace: false });

  // (24-08-2026) Both cards were wrong in the same two ways: the amount came
  // from `totalAmountCents`, a currency-BLIND sum, rendered with a hard-coded
  // "GBP"; and the open figure was summed from the rows of the CURRENT PAGE, so
  // it silently stopped growing past `pageSize`. The backend now returns a
  // per-currency split and a server-wide open total.
  const totals = useMemo(() => resolveInvoiceTotals(list.data), [list.data]);
  const mixedCurrency = isMixedCurrency(totals);

  const downloadPdf = async (id: string) => {
    setDownloadingId(id);
    try {
      const res = await apiClient.get(`${invoiceEndpoints.byId(id)}/pdf`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `invoice_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Download failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const exportZip = async () => {
    setExporting(true);
    try {
      const url = adminInvoicesExportUrl({
        from: params.from,
        to: params.to,
        company_id: params.company_id,
        period: params.period as InvoicePeriod | undefined,
      });
      const res = await apiClient.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "invoices_export.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Export failed",
        description:
          err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<AdminInvoiceRow>[]>(
    () => [
      {
        id: "invoice",
        header: "Invoice",
        cell: ({ row }) => {
          const inv = row.original;
          if (isGrouped) {
            return (
              <p className="truncate font-mono text-xs text-foreground">
                {inv.id}
              </p>
            );
          }
          const label = invoiceOwnerLabel(inv);
          return (
            <div className="flex items-center gap-3">
              <Avatar
                src={inv.ownerAvatarUrl}
                initials={getInitials(
                  inv.ownerName?.split(" ")[0],
                  inv.ownerName?.split(" ").slice(1).join(" "),
                  inv.ownerEmail,
                )}
                alt=""
                size="md"
                tone="brand"
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{label}</p>
                <p className="truncate font-mono text-xs text-muted-foreground">
                  {inv.id}
                </p>
              </div>
            </div>
          );
        },
      },
      ...(isGrouped
        ? []
        : [
            {
              id: "company",
              header: "Company",
              cell: ({ row }) => {
                const name = row.original.companyName;
                return name ? (
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate">{name}</span>
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">Personal</span>
                );
              },
            } satisfies ColumnDef<AdminInvoiceRow>,
          ]),
      {
        id: "date",
        header: "Date",
        cell: ({ row }) => {
          const at = invoiceCreatedAt(row.original);
          return (
            <span className="text-xs text-muted-foreground">
              {at ? formatDateTime(at) : "—"}
            </span>
          );
        },
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => (
          // Collected vs billed, refunds (18-10-2026, shared/billing/moneyStatus).
          <span className="text-sm font-medium">
            <MoneyAmount
              row={row.original as MoneyRowLike}
              currency={row.original.currency ?? "GBP"}
            />
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        // Paid / part paid / failed / refunded, plus Deleted and Disputed
        // markers (18-10-2026): the same reading on every money screen.
        cell: ({ row }) => <MoneyBadges row={row.original as MoneyRowLike} />,
      },
      {
        id: "billing",
        header: "Billing",
        cell: ({ row }) =>
          row.original.billedToCompany ? (
            <Badge variant="success">Corporate</Badge>
          ) : (
            <Badge variant="outline">Personal</Badge>
          ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const inv = row.original;
          const busy = downloadingId === inv.id;
          return (
            <div
              className="flex items-center justify-end gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <InvoiceMenu
                invoiceId={inv.id}
                status={inv.status}
                align="end"
                open={previewId === inv.id}
                onOpenChange={(open) => setPreviewId(open ? inv.id : null)}
              >
                <Button type="button" variant="ghost" size="icon" title="View invoice">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </InvoiceMenu>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                title="Download PDF"
                disabled={busy}
                onClick={() => void downloadPdf(inv.id)}
              >
                {busy ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            </div>
          );
        },
      },
    ],
    [downloadingId, isGrouped, previewId],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 basis-72">
            <h2 className="text-xl font-semibold tracking-tight">Invoices</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Platform-wide invoices across every company. Filter by tenant,
              period, or search; preview HTML or download PDFs; export a ZIP.
            </p>
          </div>
          {/* The filters, top right (DES 01-10-2026): text pills, the date
              circle (a custom range overrides the period), then the action. */}
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <AdminInvoicesHeaderFilters value={params} onChange={setParams} />
            <DateRangePicker
              variant="circle"
              from={params.from}
              to={params.to}
              onChange={({ from, to }) =>
                setParams({
                  ...params,
                  from,
                  to,
                  period: from || to ? undefined : params.period,
                  page: 1,
                })
              }
            />
            <Button
              type="button"
              variant="outline"
              disabled={exporting || list.isLoading}
              onClick={() => void exportZip()}
            >
              {exporting ? (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileArchive className="mr-2 h-4 w-4" />
              )}
              Export ZIP
            </Button>
          </div>
        </GridBand>

        <GridCells className="grid-cols-1 sm:grid-cols-2">
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Total invoiced (filtered)"
              icon={PoundSterling}
              tone="brand"
              value={formatMoneyMinor(totals.amountCents, totals.currency)}
              hint={
                mixedCurrency
                  ? `Plus ${totals.others
                      .map((c) => formatMoneyMinor(c.amountCents, c.currency))
                      .join(", ")} in other currencies`
                  : "Sum of invoice amounts matching current filters"
              }
              isLoading={list.isLoading}
            />
          </GridCell>
          <GridCell padded={false}>
            <StatCard
              variant="cell"
              label="Open (filtered)"
              icon={Receipt}
              tone="warning"
              value={formatMoneyMinor(totals.openAmountCents, totals.currency)}
              hint="Uncaptured across every matching row, not just this page"
              isLoading={list.isLoading}
            />
          </GridCell>
        </GridCells>

        <AdminInvoicesDateNote value={params} />

        <GridCell padded={false} className="border-b-0">
          <DataTable<AdminInvoiceRow, unknown>
            variant="cell"
            columns={columns}
            data={rows}
            groups={groups}
            expandAllGroups={searching}
            expandedGroupKeys={isGrouped ? [...openGroups.keys()] : undefined}
            groupNestedStyle={isGrouped ? "timeline" : "plain"}
            groupItemLabel="invoices"
            onGroupExpandChange={(key, expanded) =>
              setOpenGroups((prev) => {
                const next = new Map(prev);
                if (expanded) next.set(key, next.get(key) ?? 1);
                else next.delete(key);
                return next;
              })
            }
            onGroupLoadMore={(key) =>
              setOpenGroups((prev) => new Map(prev).set(key, (prev.get(key) ?? 1) + 1))
            }
            isLoading={isGrouped ? summary.isLoading : list.isLoading}
            isFetching={isGrouped ? summary.isFetching : list.isFetching}
            error={isGrouped ? summary.error : list.error}
            onRetry={() => (isGrouped ? summary.refetch() : list.refetch())}
            rowKey={(i) => i.id}
            pagination={pagination}
            onPageChange={(page) => setParams({ ...params, page })}
            onLimitChange={(pageSize) =>
              setParams({ ...params, pageSize, page: 1 })
            }
            onRowClick={(inv) => setPreviewId(inv.id)}
            emptyShape="invoice"
            emptyTitle="No invoices in this period"
            emptyDescription="Invoices appear when sessions, meetings, or notes complete with a billable charge. Try All time or clear the company filter."
          />
        </GridCell>
      </GridBlock>

      {/* The search, docked at the bottom like Notes (DES 01-10-2026). */}
      <PageDock>
        <AdminInvoicesFilters value={params} onChange={setParams} />
      </PageDock>

    </div>
  );
}
